import React, { useRef, useState, useEffect } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Tooltip,
  Alert,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { InsertEmoticon, Mic, Send, AutoAwesome, InfoOutlined } from "@mui/icons-material";
import ChannelSelector, { INTERNAL_NOTE_CHANNEL_ID } from "./ChannelSelector";
import { getChannelById, getChannelComposerSurfaceSx } from "../../utils/utils";
import { useChannels } from "../../hooks/useChannels";
import ComposeAttachMenu from "./ComposeAttachMenu";
import { useComposeSelectionFormat } from "../../hooks/useComposeSelectionFormat";
import { composerHasSendPayload } from "./composeAttachmentHelpers";
import {
  COMPOSER_MOBILE_ACTION_BUTTON_SIZE_PX,
  COMPOSER_MOBILE_ROW_HEIGHT_PX,
  COMPOSER_ROW_HEIGHT_PX,
  composerMobileInputRowSx,
  composerPrimaryActionButtonSx,
} from "./composerConstants";
import { COMPOSER_MOBILE_TEXT_FIELD_SX, COMPOSER_TEXT_FIELD_SX } from "./composerTextFieldStyles";
import ComposerMobileToolbar from "./ComposerMobileToolbar";
import RecordingIndicator from "./RecordingIndicator";
import AudioPreview from "./AudioPreview";
import { getMetaChannelLabel } from "../../utils/metaMessagingWindowUtils";

const MessageMessengerChannel = ({
  message,
  setMessage,
  channelType,
  selectedChannel,
  setSelectedChannel,
  formatOptions,
  isRecording,
  recordingDuration = 0,
  startRecording,
  stopRecording,
  discardRecording = () => {},
  openFilePicker,
  handleEmojiPickerOpen,
  handleSendMessage,
  file,
  files = [],
  audioURL,
  onRemoveAudio,
  onGenerateSuggestion,
  isGeneratingSuggestion = false,
  hasHistory = false,
  onStartVideo,
  videoSending = false,
  videoSendDisabled = false,
  videoSendDisabledHelp = null,
  videoMenuLabel = null,
  onJoinVideo,
  joinVideoSending = false,
  showJoinVideo = false,
  onStartGoogleMeet,
  googleMeetSending = false,
  googleMeetDisabled = false,
  googleMeetDisabledHelp = null,
  /** @type {{ status: string, hoursLeft?: number|null } | null} */
  metaWindowState = null,
  clientConversation = null,
  conversationMessages = null,
}) => {
  const inputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const inputBorderRadius = 16;
  const composerRowHeight = isMobile ? COMPOSER_MOBILE_ROW_HEIGHT_PX : COMPOSER_ROW_HEIGHT_PX;
  const { data: channelsData } = useChannels();
  const channels = channelsData?.channels;
  const currentChannel = getChannelById(channels, selectedChannel);
  const channelName = currentChannel?.nome || currentChannel?.name || null;
  const isInternalNote =
    String(selectedChannel) === String(INTERNAL_NOTE_CHANNEL_ID) || channelType === "Xpro";
  const composerSurface = getChannelComposerSurfaceSx(theme, {
    channelType,
    channelName,
    isInternalNote,
  });
  const canUseAiSuggestion = !!onGenerateSuggestion && channelType !== "Xpro";
  const aiDisabled = isGeneratingSuggestion || !hasHistory;
  const windowExpired = metaWindowState?.status === "expired";
  const canSend =
    !windowExpired && composerHasSendPayload(message, file, files, audioURL);
  const [windowAlertDismissed, setWindowAlertDismissed] = useState(false);

  useEffect(() => {
    setWindowAlertDismissed(false);
  }, [metaWindowState?.status]);

  const channelLabel = getMetaChannelLabel(channelType);
  const hoursLeftRounded =
    metaWindowState?.hoursLeft != null
      ? Math.max(1, Math.floor(metaWindowState.hoursLeft))
      : null;

  const showWindowBanner =
    metaWindowState &&
    (metaWindowState.status === "aboutToExpire" || metaWindowState.status === "expired") &&
    !windowAlertDismissed;

  const placeholder = (() => {
    if (channelType === "Xpro") return "Registrar uma anotação";
    if (windowExpired) {
      return `Janela de 24h do ${channelLabel} expirada — aguarde o cliente enviar uma nova mensagem`;
    }
    return `Envie uma mensagem via ${channelType}`;
  })();

  const { composeInputHandlers, selectionToolbar } = useComposeSelectionFormat({
    inputRef,
    setMessage,
    channelType,
    formatOptions,
  });

  const textField = (
    <TextField
      fullWidth
      multiline
      minRows={1}
      maxRows={4}
      variant="outlined"
      placeholder={placeholder}
      value={message}
      onChange={(e) => setMessage(e.target.value)}
      disabled={windowExpired}
      inputProps={composeInputHandlers}
      onKeyDown={(e) => {
        if (windowExpired) return;
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          handleSendMessage();
        }
      }}
      InputProps={{
        style: { borderRadius: 0, fontSize: isMobile ? "16px" : "1rem" },
        inputRef: inputRef,
        startAdornment: isMobile ? undefined : (
          <InputAdornment position="start" sx={{ ml: 0.5 }}>
            <IconButton
              onClick={handleEmojiPickerOpen}
              edge="start"
              size="medium"
              aria-label="Emoji"
              disabled={windowExpired}
            >
              <InsertEmoticon fontSize="medium" />
            </IconButton>
          </InputAdornment>
        ),
        endAdornment: isMobile ? undefined : (
          <InputAdornment position="end" sx={{ mr: 0.25, display: "flex", alignItems: "center", gap: 0 }}>
            {canUseAiSuggestion ? (
              <Tooltip
                title={
                  windowExpired
                    ? `Janela de 24h do ${channelLabel} expirada. Aguarde o cliente enviar uma nova mensagem.`
                    : aiDisabled
                      ? isGeneratingSuggestion
                        ? "Gerando resposta..."
                        : "Disponível após a conversa ter histórico."
                      : "Sugerir resposta com IA"
                }
              >
                <span>
                  <IconButton
                    onClick={onGenerateSuggestion}
                    disabled={aiDisabled || windowExpired}
                    size="medium"
                  >
                    <AutoAwesome fontSize="medium" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            <ComposeAttachMenu
              openFilePicker={openFilePicker}
              showAttachProducts={false}
              showLocation={false}
              fileSendDisabled={windowExpired}
              fileSendDisabledHelp={
                windowExpired
                  ? `Janela de 24h do ${channelLabel} expirada. Aguarde o cliente enviar uma nova mensagem.`
                  : null
              }
              showStartVideo={typeof onStartVideo === "function"}
              onStartVideo={onStartVideo}
              videoSending={videoSending}
              videoSendDisabled={videoSendDisabled || windowExpired}
              videoSendDisabledHelp={
                windowExpired
                  ? `Janela de 24h do ${channelLabel} expirada. Aguarde o cliente enviar uma nova mensagem.`
                  : videoSendDisabledHelp
              }
              videoMenuLabel={videoMenuLabel}
              showJoinVideo={showJoinVideo && typeof onJoinVideo === "function"}
              onJoinVideo={onJoinVideo}
              joinVideoSending={joinVideoSending}
              showGoogleMeet={typeof onStartGoogleMeet === "function"}
              onStartGoogleMeet={onStartGoogleMeet}
              googleMeetSending={googleMeetSending}
              googleMeetDisabled={googleMeetDisabled || windowExpired}
              googleMeetDisabledHelp={
                windowExpired
                  ? `Janela de 24h do ${channelLabel} expirada. Aguarde o cliente enviar uma nova mensagem.`
                  : googleMeetDisabledHelp
              }
              size="medium"
            />
          </InputAdornment>
        ),
      }}
      sx={isMobile ? COMPOSER_MOBILE_TEXT_FIELD_SX : COMPOSER_TEXT_FIELD_SX}
    />
  );

  const textOrVoiceSlot = isRecording ? (
    <RecordingIndicator
      embedded
      durationSeconds={recordingDuration}
      onStop={stopRecording}
      onDiscard={discardRecording}
    />
  ) : audioURL ? (
    <AudioPreview embedded audioURL={audioURL} onRemove={onRemoveAudio} />
  ) : (
    textField
  );

  const integratedComposer = (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignItems: "stretch",
        borderRadius: `${inputBorderRadius}px`,
        minHeight: composerRowHeight,
        overflow: "hidden",
        ...composerSurface.shell,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          alignSelf: "stretch",
          px: 0.25,
          ...composerSurface.divider,
        }}
      >
        <ChannelSelector
          integrated
          selectedChannel={selectedChannel}
          setSelectedChannel={setSelectedChannel}
          clientConversation={clientConversation}
          conversationMessages={conversationMessages}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: `${composerRowHeight}px`,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {textOrVoiceSlot}
      </Box>
    </Box>
  );

  const bannerSeverity = "warning";

  return (
    <Box sx={{ position: "relative", width: "100%", display: "flex", flexDirection: "column", paddingBottom: 0 }}>
      {showWindowBanner && (
        <Alert
          severity={bannerSeverity}
          icon={<InfoOutlined sx={{ fontSize: 22 }} />}
          onClose={() => setWindowAlertDismissed(true)}
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: "100%",
            mb: 1,
            py: 1.5,
            px: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "warning.light",
            zIndex: 1500,
            "& .MuiAlert-message": { width: "100%", padding: 0 },
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: "warning.dark",
            }}
          >
            {metaWindowState.status === "aboutToExpire"
              ? `Janela de 24 horas do ${channelLabel} quase fechando`
              : `Janela de 24 horas do ${channelLabel} expirada`}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.55 }}>
            {metaWindowState.status === "aboutToExpire" ? (
              <>
                Restam aproximadamente <strong>{hoursLeftRounded} {hoursLeftRounded === 1 ? "hora" : "horas"}</strong>{" "}
                para responder. Depois disso, será necessário aguardar o cliente enviar uma nova mensagem para
                reabrir o atendimento.
              </>
            ) : (
              <>
                Passaram-se mais de 24 horas desde a última mensagem do cliente. Não é possível enviar por{" "}
                {channelLabel}. Peça ao cliente para enviar uma nova mensagem para reabrir o atendimento.
              </>
            )}
          </Typography>
        </Alert>
      )}
      <Box sx={isMobile ? composerMobileInputRowSx : { display: "flex", alignItems: "center", gap: 0, width: "100%" }}>
        {integratedComposer}
        {isMobile && !isRecording && !audioURL ? (
        <ComposerMobileToolbar>
          <IconButton
            onClick={handleEmojiPickerOpen}
            size="small"
            aria-label="Emoji"
            disabled={windowExpired}
          >
            <InsertEmoticon fontSize="small" />
          </IconButton>
          {canUseAiSuggestion ? (
            <Tooltip
              title={
                windowExpired
                  ? `Janela de 24h do ${channelLabel} expirada. Aguarde o cliente enviar uma nova mensagem.`
                  : aiDisabled
                    ? isGeneratingSuggestion
                      ? "Gerando resposta..."
                      : "Disponível após a conversa ter histórico."
                    : "Sugerir resposta com IA"
              }
            >
              <span>
                <IconButton
                  onClick={onGenerateSuggestion}
                  disabled={aiDisabled || windowExpired}
                  size="small"
                >
                  <AutoAwesome fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          <ComposeAttachMenu
            openFilePicker={openFilePicker}
            showAttachProducts={false}
            showLocation={false}
            fileSendDisabled={windowExpired}
            fileSendDisabledHelp={
              windowExpired
                ? `Janela de 24h do ${channelLabel} expirada. Aguarde o cliente enviar uma nova mensagem.`
                : null
            }
            showStartVideo={typeof onStartVideo === "function"}
            onStartVideo={onStartVideo}
            videoSending={videoSending}
            videoSendDisabled={videoSendDisabled || windowExpired}
            videoSendDisabledHelp={
              windowExpired
                ? `Janela de 24h do ${channelLabel} expirada. Aguarde o cliente enviar uma nova mensagem.`
                : videoSendDisabledHelp
            }
            videoMenuLabel={videoMenuLabel}
            showJoinVideo={showJoinVideo && typeof onJoinVideo === "function"}
            onJoinVideo={onJoinVideo}
            joinVideoSending={joinVideoSending}
            showGoogleMeet={typeof onStartGoogleMeet === "function"}
            onStartGoogleMeet={onStartGoogleMeet}
            googleMeetSending={googleMeetSending}
            googleMeetDisabled={googleMeetDisabled || windowExpired}
            googleMeetDisabledHelp={
              windowExpired
                ? `Janela de 24h do ${channelLabel} expirada. Aguarde o cliente enviar uma nova mensagem.`
                : googleMeetDisabledHelp
            }
            size="small"
          />
        </ComposerMobileToolbar>
        ) : null}
        {isMobile ? (
          <IconButton
            onClick={() => {
              if (windowExpired) return;
              if (canSend) handleSendMessage();
              else startRecording();
            }}
            disabled={windowExpired}
            aria-label={canSend ? "Enviar" : "Gravar áudio"}
            sx={composerPrimaryActionButtonSx({ size: COMPOSER_MOBILE_ACTION_BUTTON_SIZE_PX })}
          >
            {canSend ? <Send fontSize="small" /> : <Mic fontSize="small" />}
          </IconButton>
        ) : null}
      </Box>
      {selectionToolbar}
    </Box>
  );
};

export default MessageMessengerChannel;
